// Chat-intent classification command (EPIC #157, phase P5.4 — design §2.2).
//
// The one I/O core of the Sayer chat flow: build the classifier prompt from the current
// forecast context, call the LLM (`deps.services.llm`, the locked-down /api/forecast/llm
// passthrough), and parse its reply into a `{ intent, params, response }` decision. Pure —
// no React, no store, no routing. The UI routing (dispatchIntent), the tour gating, and the
// chat entry point stay in `hooks/useChatIntent.js`, which calls this and acts on the result.
//
// The prompt template is moved VERBATIM out of App.jsx's `classifyAndDispatch`; only the
// interpolated context is parameterized (assembled by the hook from the live store + series).
import type { CommandDeps } from "./deps.js";
import { todayAnchor } from "../lib/util/todayAnchor.js";
import { webSearchClause } from "../lib/forecast/webSearchClause.js";
import { earlierConversationClause } from "../lib/chat/chatMemory.js";
import { howSayerWorksClause } from "../lib/onboarding/tipEngine.js";

/** The forecast context the classifier prompt is built from (assembled by the hook). */
export interface ChatIntentContext {
  contractQuestion: string;
  probabilityPct: number;
  /** The answer as the header states it ("97% chance of No"); absent when there is none. */
  forecastAnswer?: string | null;
  resolutionDate: string;
  eventsList: string;
  branchesList: string;
  indicesList: string;
  currentEngineLabel: string;
  recentMessages: string;
  /** The chat about other questions, grouped by question; "" when there is none. */
  earlierConversation?: string;
  // Report-derived context (all optional — a report-less/multi forecast omits them), so the
  // classifier can answer "why NN%?", "strongest driver?", "which sources?". See reportContext.
  //
  // These are the FULL report now, not the old 600-char / 3-reason clip: a production report
  // carries >=10 strength-rated reasons a side with citations, the resolution-criteria
  // breakdown, the weighing observations, and the calibration walk. The modal always rendered
  // all of it; the chat saw a sliver, which is why Sayer could describe a forecast but not
  // reason about it. `reportContext.js` owns the (generous, but bounded) caps.
  reportSummary?: string;
  reportRephrased?: string;
  reportCriteria?: string;
  reasonsFor?: string;
  reasonsAgainst?: string;
  reportAggregation?: string;
  reportCalibration?: string;
  reportEvidence?: string;
  sourcesList?: string;
  /** How-to text for questions about using the app (onboarding/tips.js); omitted when off. */
  howSayerWorks?: string;
  // Multi-value (distribution) context — null/absent for a binary forecast. See
  // `distributionContext.js`. When `isDistribution` is true the prompt swaps the whole
  // binary framing: no P(YES) line, the PMF in its place, and the distribution's own drivers
  // instead of Why YES / Why NO.
  dist?: ChatIntentDistributionContext | null;
  /** Whether this request actually carries the web-search plugin (see `webSearchClause`). */
  webSearch?: boolean;
}

/** The distribution half of the forecast context — `forecastDistributionContext`'s output. */
export interface ChatIntentDistributionContext {
  isDistribution: boolean;
  kind: string;
  buckets: string;
  leading: string;
  shape: string;
  summary: string;
  considerations: string;
  perOutcome: string;
  driversFor: string;
  driversAgainst: string;
  spreadDrivers: string;
  referenceClass: string;
  confidence: string;
  sources: string;
  forLabel: string;
  againstLabel: string;
}

/** The classifier's parsed decision (shape the LLM is asked to emit). */
export interface ChatIntent {
  intent: string;
  params?: Record<string, any>;
  response?: string;
}

/** What the command returns: the parsed decision (or null if unparseable) + the raw reply. */
export interface ClassifyResult {
  parsed: ChatIntent | null;
  raw: string;
}

export interface ClassifyArgs {
  question: string;
  context: ChatIntentContext;
  signal?: AbortSignal | null;
  /** The chat turn this classification belongs to (lib/chatTurn.js) — timing only. */
  turnId?: string | null;
}

/** One "- Label:\n  body" prompt section, or "" when the body is empty. */
function section(label: string, body?: string): string {
  return body && body.trim() ? `- ${label}:\n${body}\n` : "";
}

/**
 * The CURRENT FORECAST prompt section, framed by question type.
 *
 * A binary forecast and a distribution are described in genuinely different vocabularies, and
 * the old block only spoke binary: it emitted `Probability: NN%` from `contract.probability`
 * — a placeholder (typically 0) on a multi run — and looked for `report.reasons_for/against`,
 * which a distribution does not have. So a multiway forecast reached the model as a
 * meaningless scalar with no outcome labels: it could not name the leading outcome, never
 * mind say why it led. Branching here keeps ONE prompt while letting each type be described
 * in its own terms.
 *
 * The two branches are mutually exclusive by construction — `forecastDistributionContext`
 * returns `isDistribution: false` for anything without a real PMF (a BooleanSpace binary
 * expressed as a two-slot PMF included) — so this never emits both framings, and the token
 * cost of full-report access is paid once, not twice.
 */
function forecastBlock(c: ChatIntentContext): string {
  const d = c.dist;
  const multi = d?.isDistribution === true;
  const head = multi
    ? `- Question type: MULTI-OUTCOME (${d?.kind || "categorical"}) — a probability distribution over mutually-exclusive outcomes, NOT a yes/no. There is no single "probability" for this forecast.
- Outcome distribution:\n${d?.buckets || "  (none)"}
${d?.leading ? `- Leading outcome: ${d.leading}\n` : ""}${d?.shape ? `- Distribution shape: ${d.shape}\n` : ""}`
    : `- Question type: BINARY yes/no.
- Probability: ${c.probabilityPct}% chance of Yes
${c.forecastAnswer ? `- Sayer's answer: ${c.forecastAnswer} (when you state the forecast, name this larger side, as the app does)\n` : ""}`;

  // Detail sections. The binary path reads the grounded report; the distribution path reads
  // multi_report + distribution_reasoning. Empty sections are dropped entirely rather than
  // rendered as "(none)" — a wall of "(none)" is noise the model has to read past, and an
  // absent section already reads as absent.
  const detail = multi
    ? section("Assessment", d?.summary) +
      section("Key considerations", d?.considerations) +
      section("Notes on specific outcomes", d?.perOutcome) +
      section(d?.forLabel || "Factors favouring the leading outcome", d?.driversFor) +
      section(d?.againstLabel || "Factors favouring the field", d?.driversAgainst) +
      section("What widens the spread", d?.spreadDrivers) +
      section("Reference class (outside view)", d?.referenceClass) +
      section("Forecaster's confidence note", d?.confidence) +
      section("Sources", d?.sources)
    : section("Assessment", c.reportSummary) +
      section("Question as the forecaster restated it", c.reportRephrased) +
      section("Resolution criteria", c.reportCriteria) +
      section("Why YES (strength-rated, strongest first)", c.reasonsFor) +
      section("Why NO (strength-rated, strongest first)", c.reasonsAgainst) +
      section("How the two sides were weighed", c.reportAggregation) +
      section("Calibration", c.reportCalibration) +
      section("Background evidence", c.reportEvidence) +
      section("Sources", c.sourcesList);

  return `CURRENT FORECAST:
- Question: "${c.contractQuestion}"
${head}- Resolution date: ${c.resolutionDate}
${detail}- Triggers (events):\n${c.eventsList || "(none)"}
- Branches:\n${c.branchesList || "(none)"}
- Active indices: ${c.indicesList}

This material IS the forecast's reasoning. When the user asks why the ${multi ? "distribution looks the way it does, what's driving an outcome" : "probability is what it is, what's driving it"}, or which sources back it, answer from the sections above (use "explain") and cite them in your own words. Do not invent facts beyond them, and do not restate or revise the ${multi ? "outcome probabilities" : "probability"} yourself.
`;
}

/**
 * Classify a chat message into a UI intent. Returns `{ parsed, raw }`: `parsed` is the
 * `{intent, params, response}` object when the LLM emitted valid JSON, else null (the
 * caller falls back to showing `raw` conversationally). Throws on LLM/transport error
 * (incl. AbortError) — the caller owns abort/error handling.
 */
export async function classifyChatIntent(
  deps: CommandDeps,
  { question, context, signal, turnId = null }: ClassifyArgs,
): Promise<ClassifyResult> {
  const { llm } = deps.services;
  const prompt = `You are Sayer, a forecasting analyst assistant. The user is interacting with a forecast timeline UI. Respond naturally and concisely like an analyst colleague.

${todayAnchor()}

${forecastBlock(context)}

${earlierConversationClause(context.earlierConversation)}RECENT CONVERSATION:
${context.recentMessages || "(none)"}

${howSayerWorksClause(context.howSayerWorks || "")}${webSearchClause(context.webSearch !== false)}

AVAILABLE ENGINES (pass any of these strings as the "engine" param):
- "Quick" / "Quick Forecast" / "fast" → fast/cheap engine
- "Deep" / "Deep Forecast" / "thorough" → slow/thorough engine
Or pass the raw orchestrator name. Currently selected: "${context.currentEngineLabel}".

AVAILABLE ACTIONS (pick ONE, or "explain" for conversation only):
- new_forecast: params {question, engine?, persistent?} — user wants a completely new unrelated forecast. If user names a specific engine for this forecast, set "engine". Set "persistent": true ONLY if they say "always" / "from now on" — otherwise the engine swap is one-shot.
- confirm_new_forecast: params {question} — the message is a forecasting-shaped question that is PLAUSIBLY a new forecast, but they didn't say "new forecast" and a forecast is already open, so you're not sure. Set params.question to the FULLY-RESOLVED forecasting question (keep an implicit-but-knowable timeframe like "after Trump leaves office" verbatim — do NOT strip it and do NOT invent an explicit date), and put the one-line confirm prompt in "response". Do NOT forecast yet — the app runs it only if the user confirms next turn.
- set_engine: params {engine, persistent?, rerun?} — user wants to switch the active engine. Set "persistent": true if they say "always" / "from now on"; otherwise it's one-shot. Set "rerun": true if they want to immediately re-run the current question with the new engine ("redo this with deep", "switch to deep and try again").
- add_event: params {description, date} — add a custom trigger event and branch
- branch_occurs: params {eventTitle} — what if this existing event happens
- branch_not_occurs: params {eventTitle} — what if this existing event doesn't happen
- add_index: params {name} — add a time series index
- remove_index: params {name} — remove a time series index
- change_indices: params {names:[]} — replace all indices
- zoom_out: no params — zoom out to widest view
- zoom_to_timeline: no params — fit entire timeline on screen
- center: no params — center on today
- set_detail: params {level} — set event detail level 1-5
- show_report: no params — open the executive report for the current forecast
- open_forecast_manager: no params — open the forecast manager / history list ("show me my past forecasts", "open the forecast manager", "let me see my saved forecasts")
- file_bug: no params — open the bug-report form ("I want to file a bug", "report a bug", "something's broken")
- open_settings: no params — open the settings panel ("open settings", "change the theme", "settings")
- open_account: no params — open the account panel ("my account", "account settings", "log out")
- open_series_panel: no params — open the time-series / indices panel ("show the indices panel", "open the series drawer")
- close_panels: no params — close any open panels/overlays ("close that", "close everything", "dismiss")
- explain: no params — just answer conversationally, no UI change
- reset: no params — clear everything and start fresh

RULES:
- An active forecast is already shown. Default to UI-control intents (zoom/center/add_index/etc), branching (branch_occurs/branch_not_occurs/add_event), or "explain" for conversational answers about the current forecast.
- **Resolve referents from RECENT CONVERSATION before filling params.** When the user's message is a confirmation, retry, or deictic phrase ("go ahead", "yes do it", "do the forecast", "run it", "proceed", "try again", "resubmit", "redo that", "do that one", "okay forecast it", "the one I just asked"), the literal user message IS NOT the content — it's a directive pointing at something earlier in the conversation. Scan RECENT CONVERSATION (most recent first) for the most recent matching content and put THAT into params:
  - For new_forecast: find the most recent user-typed forecasting question (the one they asked before cancelling, editing, or hitting an error) and use it as params.question. NEVER echo the confirmation phrase itself as params.question — a question like "go ahead and do the forecast" has no timeframe and will be rejected by the submit guard, leaving the user stuck.
  - For branch_occurs / branch_not_occurs: find the event title the user was just discussing and use it as params.eventTitle.
  - For add_event: find the hypothetical the user just proposed and use it as params.description (+ date).
  If RECENT CONVERSATION doesn't contain a viable referent, fall back to "explain" with a response asking the user to restate what they want forecasted/branched — do not invent one.
- new_forecast triggers:
  - **Explicit phrasing** ("new forecast", "start a new forecast", "new question, forecast this:", "forecast X with deep mode") → use new_forecast directly. Set params.question to the question to forecast.
  - **Ambiguous but plausible** (the message is a forecasting-shaped question, OR mentions a topic clearly unrelated to the current forecast, but they didn't use the words "new forecast") → DO NOT silently switch to explain, and DO NOT use new_forecast yet. Use **confirm_new_forecast** with params.question set to the resolved question and a "response" like: "It sounds like you might want me to forecast that as a new question — should I run a fresh forecast on '${"<paraphrase>"}', or are you asking about the current one?" The app stores the question and runs it if the user confirms next turn — you do NOT need to re-resolve it on the confirmation turn.
  - **A forecasting-shaped question is NOT only yes/no.** A multi-outcome question — "how many X …", "how much …", "which of … will …", "who will win …", "what will <metric> be …" (with a timeframe) — is equally a forecast: the system forecasts a probability distribution over the outcomes. Treat these EXACTLY like a yes/no forecasting question (new_forecast if explicitly new, else confirm_new_forecast). Do NOT deflect a multi-outcome question to "explain", and NEVER tell the user to rephrase it as a yes/no question — numeric and categorical questions are fully supported.
  - **Pure conversational** ("why is this probability so low?", "what does the resolution date mean?") → use "explain". No confirmation needed.
- Branching triggers:
  - **Explicit phrasing** (uses the words "branch", "timeline", "fork", "scenario", "what if", "alternate path") → use branch_occurs / branch_not_occurs / add_event directly. branch_occurs/branch_not_occurs require eventTitle to match an existing event; otherwise use add_event with description + date.
  - **Ambiguous but plausible** (the user proposes a hypothetical or counterfactual but doesn't use those words — e.g. "imagine if Trump loses", "what would happen if rates drop") → DO NOT silently fall through to explain. Use "explain" with a response that asks: "Want me to branch the forecast on '${"<that scenario>"}'? I'll fork the timeline with that as a trigger." Wait for confirmation; on yes, fire the appropriate branch intent.
  - **Pure conversational** about an event ("when does this event happen?", "what's driving this one?") → use "explain".
- For engine swaps: "use deep on this" / "redo this with quick mode" → set_engine with rerun:true. "switch to deep" / "use deep from now on" → set_engine with persistent:true. "Forecast X with deep mode" → new_forecast with engine and question. Default persistent to false unless user clearly asks for permanent change.
- If the user asks a question about the forecast, its probability, or the events, use "explain" and answer it.
- For dates, use YYYY-MM-DD format. If the user is vague about dates, pick a reasonable one based on context.
- Keep your response under 3 sentences unless the user asks for detail.

USER MESSAGE: "${question}"

Reply with ONLY valid JSON (no markdown, no backticks):
{"intent":"...","params":{...},"response":"your conversational reply"}`;

  // `webSearch` reaches the TRANSPORT here and the PROMPT above from the same field, so the
  // request and the instructions can never disagree about whether the tool exists.
  const text = await llm(prompt, { signal, webSearch: context.webSearch !== false, turnId });
  let parsed: ChatIntent | null;
  try {
    const jsonMatch = text.match(/\{[\s\S]*\}/);
    parsed = jsonMatch ? JSON.parse(jsonMatch[0]) : null;
  } catch (_) {
    parsed = null;
  }
  return { parsed, raw: text };
}
